// src/components/ProductCard.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { API_BASES } from '../config';
import { IMG_FALLBACK, onImgError } from '../utils/imageFallback';

const FILE_SERVICE_BASE_URL = API_BASES.files;

// Seeded pseudo-random for consistent per-product fake stats
const seeded = (seed) => {
  const x = Math.sin(seed + 1) * 10000;
  return x - Math.floor(x);
};

const getFakeRating = (id) => (4.0 + seeded(id * 3) * 1.0).toFixed(1);
const getFakeDownloads = (id) => {
  const n = Math.round(50 + seeded(id * 7) * 1150);
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;
};

const StarRating = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  return (
    <span className="pc-stars" aria-label={`${rating} estrellas`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className={i <= full ? 'pc-star filled' : half && i === full + 1 ? 'pc-star half' : 'pc-star empty'}
        >
          ★
        </span>
      ))}
    </span>
  );
};

export default function ProductCard({ product }) {
  const productName = product.nombre ?? product.title ?? 'Nombre Desconocido';
  const priceNum = Number(product.precioIndividual ?? product.price ?? 0);
  const productCountry = product.pais ?? '—';
  const isFree = Boolean(product.esGratis) || priceNum === 0;

  const specialty =
    Array.isArray(product.especialidades) && product.especialidades.length
      ? product.especialidades[0]
      : Array.isArray(product.categorias) && product.categorias.length
      ? product.categorias[0]
      : 'General';

  // --- Portada: solo la 1ra foto disponible ---
  let coverUrl = IMG_FALLBACK;

  if (Array.isArray(product.fotografiaUrl) && product.fotografiaUrl.length > 0) {
    coverUrl = product.fotografiaUrl[0];
  } else if (typeof product.fotografiaUrl === 'string' && product.fotografiaUrl.trim() !== '') {
    coverUrl = product.fotografiaUrl;
  } else if (Array.isArray(product.fotografiaProd) && product.fotografiaProd.length > 0) {
    coverUrl = `${FILE_SERVICE_BASE_URL}/${product.idProducto}/${product.fotografiaProd[0]}`;
  }

  const rating = parseFloat(getFakeRating(product.idProducto));
  const downloads = getFakeDownloads(product.idProducto);

  return (
    <div className="pc-card">
      {/* Image */}
      <div className="pc-img-wrap">
        <img
          src={coverUrl}
          className="pc-img"
          alt={productName}
          onError={onImgError}
        />
      </div>

      {/* Body */}
      <div className="pc-body">
        {/* Meta: specialty · country */}
        <p className="pc-meta">
          {specialty.toUpperCase()} · {productCountry.toUpperCase()}
        </p>

        {/* Title */}
        <h5 className="pc-title">{productName}</h5>

        {/* Rating row */}
        <div className="pc-rating-row">
          <StarRating rating={rating} />
          <span className="pc-rating-num">{rating}</span>
          <span className="pc-downloads">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 5v14M5 12l7 7 7-7"/>
            </svg>
            {downloads} descargas
          </span>
        </div>

        {/* Price + CTA */}
        <div className="pc-footer">
          {isFree ? (
            <span className="pc-price-free">Free</span>
          ) : (
            <span className="pc-price">${priceNum.toFixed(2)}</span>
          )}

          <Link
            to={`/producto/${product.idProducto}`}
            className={isFree ? 'pc-btn pc-btn-dark' : 'pc-btn pc-btn-yellow'}
          >
            {isFree ? 'Descargar' : 'Ver detalles'}
          </Link>
        </div>
      </div>
    </div>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    idProducto: PropTypes.number.isRequired,
    nombre: PropTypes.string,
    title: PropTypes.string,
    precioIndividual: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    esGratis: PropTypes.bool,
    especialidades: PropTypes.arrayOf(PropTypes.string),
    categorias: PropTypes.arrayOf(PropTypes.string),
    pais: PropTypes.string,
    fotografiaUrl: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string,
    ]),
    fotografiaProd: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};
